
import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send } from 'lucide-react';
import ChatMessage, { ChatMessageProps } from './ChatMessage';
import { useLanguage } from '@/contexts/language';
import { t } from '@/utils/translations';

interface ChatbotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ChatbotDialog: React.FC<ChatbotDialogProps> = ({ open, onOpenChange }) => {
  const { language } = useLanguage();
  const [messages, setMessages] = useState<ChatMessageProps[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initialize messages with greeting from translations when dialog opens
  useEffect(() => {
    if (open) {
      setMessages([{
        type: 'assistant',
        content: t('chatbot.greeting', language)
      }]);
    }
  }, [open, language]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
    
    // Add user message
    const userMessage: ChatMessageProps = {
      type: 'user',
      content: inputValue.trim()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    
    // Simulate API response delay
    setTimeout(() => {
      const botResponse = generateBotResponse(inputValue.trim().toLowerCase());
      setMessages(prev => [...prev, {
        type: 'assistant',
        content: botResponse
      }]);
      setIsTyping(false);
    }, 1500);
  };

  const generateBotResponse = (query: string): string => {
    // Check for baby milk/formula related questions
    if (query.includes('milk') || query.includes('formula') || query.includes('bottle') || 
        query.includes('breast') || query.includes('breastfeeding') || query.includes('feed baby') ||
        query.includes('how to feed') || query.includes('make milk')) {
      return t('chatbot.responses.babyMilk', language);
    }
    
    // Check for baby crying related questions
    if (query.includes('crying') || query.includes('stop cry') || query.includes('cries') || 
        query.includes('calm baby') || query.includes('soothe') || query.includes('fussy') ||
        query.includes('won\'t stop crying') || query.includes('how to stop') || 
        query.includes('make the baby stop')) {
      return t('chatbot.responses.babyCrying', language);
    }
    
    // Check for general baby care questions
    if ((query.includes('care') || query.includes('take care') || query.includes('bath') || 
        query.includes('diaper') || query.includes('dress') || query.includes('nail') ||
        query.includes('how to') || query.includes('handle')) && 
        (query.includes('baby') || query.includes('infant') || query.includes('newborn'))) {
      return t('chatbot.responses.generalBabyCare', language);
    }
    
    // Check for pregnancy-related questions
    if (query.includes('pregnancy') || query.includes('pregnant') || query.includes('trimester') || 
        query.includes('expecting') || query.includes('morning sickness') || query.includes('prenatal')) {
      return t('chatbot.responses.pregnancy', language);
    }
    
    // Check for baby development questions
    if ((query.includes('baby') || query.includes('infant')) && 
        (query.includes('develop') || query.includes('growth') || query.includes('growing'))) {
      return t('chatbot.responses.babyDevelopment', language);
    }
    
    // Check for baby milestone questions
    if (query.includes('milestone') || query.includes('first step') || 
        query.includes('first word') || query.includes('crawl') || query.includes('sit up') || 
        query.includes('roll over')) {
      return t('chatbot.responses.babyMilestones', language);
    }
    
    // Check for baby feeding questions
    if (query.includes('feed') || query.includes('eating') || 
        query.includes('solid food') || query.includes('weaning')) {
      return t('chatbot.responses.feeding', language);
    }
    
    // Check for baby sleep questions
    if (query.includes('sleep') || query.includes('nap') || query.includes('bedtime') || 
        query.includes('night') || query.includes('waking up') || query.includes('crib')) {
      return t('chatbot.responses.sleep', language);
    }
    
    // Check for vaccination questions
    if (query.includes('vaccine') || query.includes('vaccination') || query.includes('shot') || 
        query.includes('immunization') || query.includes('booster')) {
      return t('chatbot.responses.vaccinations', language);
    }
    
    // Check for postpartum questions
    if (query.includes('postpartum') || query.includes('after birth') || query.includes('after delivery') || 
        query.includes('recovery') || query.includes('postnatal')) {
      return t('chatbot.responses.postpartum', language);
    }
    
    // Simple pattern matching for general queries
    if (query.includes('hello') || query.includes('hi') || query.includes('hey')) {
      return "Hello! How can I help you today with your baby or pregnancy questions?";
    } else if (query.includes('navigate') || query.includes('use') || query.includes('how to use app')) {
      return "To navigate our app, use the menu at the top to access different sections. You can create diary entries, view your entries, check growth statistics, and more!";
    } else if (query.includes('diary') || query.includes('entries')) {
      return "You can create beautiful diary entries by clicking on 'Create Entry' from the homepage or navigating to the Generate page from the menu.";
    } else if (query.includes('thank')) {
      return "You're welcome! I'm happy to help. Is there anything else you'd like to know about your baby or pregnancy?";
    } else {
      return "I'm not sure I understand your question. Could you rephrase it? I can help with pregnancy information, baby development, milestones, feeding, crying, baby care, sleep, vaccinations, or app navigation.";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md md:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('chatbot.title', language)}</DialogTitle>
          <DialogDescription>
            {t('chatbot.subtitle', language)}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto py-4 px-1 max-h-[50vh]">
          {messages.map((message, i) => (
            <ChatMessage 
              key={i} 
              type={message.type} 
              content={message.content}
              isTyping={i === messages.length - 1 && isTyping && message.type === 'assistant'}
            />
          ))}
          
          {isTyping && (
            <ChatMessage 
              type="assistant" 
              content="" 
              isTyping={true}
            />
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        <div className="flex items-center space-x-2 pt-4 border-t">
          <Input
            placeholder={t('chatbot.typeMessage', language)}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSendMessage();
            }}
            className="flex-1"
          />
          <Button onClick={handleSendMessage} size="icon" disabled={!inputValue.trim() || isTyping}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChatbotDialog;
